import { ApolloClient } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { GUILD_AGE_RECOMMENDATIONS } from '@/constants/guildAgeRecommendations'
import { CONTENT_RATING_SOURCE } from '@/services/ContentRatingService'
import { GetRecommendedAgeBySlugQuery, GetRecommendedAgeBySlugQueryVariables, Rating } from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import { Maybe } from '@/utils/types'
import { GET_RECOMMENDED_AGE_BY_SLUG } from './queries'

export interface GuildRating {
  ageText: string
  age: number
  description?: string | null
}

/**
 * Fetches guild age rating information for a given project slug.
 * This is currently a hybrid service that checks both local constants
 * and the federation graph API.
 *
 * @param projectSlug - The slug of the project to get rating for
 * @param client - An instance of the Apollo web client
 * @returns Promise resolving to guild rating data or null if not found
 */
export const getGuildRatingBySlug = async (projectSlug: string, client: ApolloClient): Promise<GuildRating | null> => {
  try {
    const { data } = await client.query<GetRecommendedAgeBySlugQuery, GetRecommendedAgeBySlugQueryVariables>({
      query: GET_RECOMMENDED_AGE_BY_SLUG,
      variables: { slug: projectSlug },
    })
    return resolveGuildRating(projectSlug, data)
  } catch (error) {
    logger().error(`Error fetching guild rating for slug ${projectSlug}:`, error)
    return null
  }
}

export const useGuildRatingBySlug = (projectSlug: string) => {
  const { data, loading, ...rest } = useQuery<GetRecommendedAgeBySlugQuery, GetRecommendedAgeBySlugQueryVariables>(
    GET_RECOMMENDED_AGE_BY_SLUG,
    {
      variables: { slug: projectSlug },
      errorPolicy: 'all',
      skip: !projectSlug,
    },
  )

  return {
    rating: !loading ? resolveGuildRating(projectSlug, data) : undefined,
    loading,
    ...rest,
  }
}

export function getGuildDescriptionFromContentRatings(contentRatings: Maybe<Maybe<Rating>[]>): string | null {
  if (!contentRatings?.length) return null

  const guildRating = contentRatings.find((rating) => rating?.source === CONTENT_RATING_SOURCE.GUILD && rating?.summary)

  return guildRating?.summary ?? null
}

function resolveGuildRating(projectSlug: string, data: GetRecommendedAgeBySlugQuery | undefined) {
  const age = data?.project?.title?.recommendedAge?.age

  if (!age) {
    logger().debug(`No recommended age found for project slug: ${projectSlug}`)
    return null
  }

  // First try to get description from contentRatings, fall back to hardcoded constants
  const contentRatings = data?.project?.title?.contentRatings
  const descriptionFromApi = getGuildDescriptionFromContentRatings(contentRatings)
  const fallbackRating = GUILD_AGE_RECOMMENDATIONS[projectSlug as keyof typeof GUILD_AGE_RECOMMENDATIONS]

  return {
    ageText: `Ages ${age}+`,
    age,
    description: descriptionFromApi ?? fallbackRating?.description ?? null,
  }
}
